import type {
  CreatePhaseDependencyRequest,
  CreatePhaseQueueGroupRequest,
  CreateProjectPlanRequest,
  ListPlanningEventsResponse,
  ListProjectPlansQuery,
  ListProjectPlansResponse,
  PhaseBlockRequest,
  ProjectPlanDetails,
  UpdateProjectPhaseRequest,
} from '@kreart/shared';
import { api } from '@/lib/api';

function serialiseListQuery(query: ListProjectPlansQuery): string {
  const p = new URLSearchParams();
  if (query.page !== 1) p.set('page', String(query.page));
  if (query.pageSize !== 100) p.set('pageSize', String(query.pageSize));
  if (query.search) p.set('search', query.search);
  if (query.status) p.set('status', query.status);
  if (query.phaseStatus) p.set('phaseStatus', query.phaseStatus);
  if (query.delayed) p.set('delayed', 'true');
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

export const projectPlanningApi = {
  list: (query: ListProjectPlansQuery) =>
    api.get<ListProjectPlansResponse>(`/project-plans${serialiseListQuery(query)}`),
  createForProject: (projectId: string, body: CreateProjectPlanRequest = {}) =>
    api.post<ProjectPlanDetails>(`/projects/${projectId}/plan`, body),
  removePlan: (planId: string) => api.delete<void>(`/project-plans/${planId}`),
  resetPlan: (planId: string) => api.post<ProjectPlanDetails>(`/project-plans/${planId}/reset`),
  getByProject: (projectId: string) => api.get<ProjectPlanDetails>(`/projects/${projectId}/plan`),
  listEvents: (planId: string) =>
    api.get<ListPlanningEventsResponse>(`/project-plans/${planId}/events`),
  updatePhase: (phaseId: string, body: UpdateProjectPhaseRequest) =>
    api.patch<ProjectPlanDetails>(`/project-phases/${phaseId}`, body),
  startPhase: (phaseId: string) => api.post<ProjectPlanDetails>(`/project-phases/${phaseId}/start`),
  completePhase: (phaseId: string) =>
    api.post<ProjectPlanDetails>(`/project-phases/${phaseId}/complete`),
  blockPhase: (phaseId: string, body: PhaseBlockRequest) =>
    api.post<ProjectPlanDetails>(`/project-phases/${phaseId}/block`, body),
  unblockPhase: (phaseId: string) =>
    api.post<ProjectPlanDetails>(`/project-phases/${phaseId}/unblock`),
  addDependency: (phaseId: string, body: CreatePhaseDependencyRequest) =>
    api.post<ProjectPlanDetails>(`/project-phases/${phaseId}/dependencies`, body),
  removeDependency: (id: string) => api.delete<void>(`/phase-dependencies/${id}`),
  createQueueGroup: (body: CreatePhaseQueueGroupRequest) =>
    api.post<{ id: string }>('/project-plans/queue-groups', body),
};
